/**
 * Who does what: one Computer and one AI choice per role.
 *
 * Auto is the stored automatic or unset state. It does not write, and it does
 * not pick a provider, runtime, model, or route. An explicit assignment is
 * shown back as itself and is rewritten only when the owner changes that choice,
 * through the same request the existing control already sends.
 */
import type { CommandPlannerHostStatus } from './data';
import {
  normalizePlacement,
  samePlacement,
  type Placement,
  type PlacementDraft,
} from './management-hosting';

export const AUTO_VALUE = '';

export const WHO_DOES_WHAT_ROLE_ORDER = ['overseer', 'planner'] as const;
export type WhoDoesWhatRole = (typeof WHO_DOES_WHAT_ROLE_ORDER)[number];

export const WHO_DOES_WHAT_ROLE_LABEL: Readonly<Record<WhoDoesWhatRole, string>> = {
  overseer: 'Overseer',
  planner: 'Planner',
};

export function placementComputerValue(placement: Placement | null | undefined): string {
  if (placement == null || placement.mode === 'automatic') return AUTO_VALUE;
  return placement.preferredNodeId ?? AUTO_VALUE;
}

/** The placement write for a Computer choice. Pinned stays pinned. Preferred keeps its fallback. */
export function placementDraftForComputer(placement: Placement, nodeId: string): PlacementDraft {
  if (nodeId === AUTO_VALUE) {
    return normalizePlacement({
      mode: 'automatic', preferredNodeId: null, fallbackPolicy: 'automatic', fallbackNodeIds: [],
    });
  }
  if (placement.mode === 'pinned') {
    return normalizePlacement({
      mode: 'pinned', preferredNodeId: nodeId, fallbackPolicy: 'none', fallbackNodeIds: [],
    });
  }
  if (placement.mode === 'preferred') {
    return normalizePlacement({
      mode: 'preferred',
      preferredNodeId: nodeId,
      fallbackPolicy: placement.fallbackPolicy,
      fallbackNodeIds: placement.fallbackNodeIds.filter((id) => id !== nodeId),
    });
  }
  return normalizePlacement({
    mode: 'preferred', preferredNodeId: nodeId, fallbackPolicy: 'automatic', fallbackNodeIds: [],
  });
}

export function placementComputerChanged(placement: Placement, nodeId: string): boolean {
  const current: PlacementDraft = {
    mode: placement.mode,
    preferredNodeId: placement.preferredNodeId,
    fallbackPolicy: placement.fallbackPolicy,
    fallbackNodeIds: placement.fallbackNodeIds,
  };
  return !samePlacement(placementDraftForComputer(placement, nodeId), current);
}

export function plannerComputerValue(status: CommandPlannerHostStatus | null): string {
  return status === null ? AUTO_VALUE : status.node_id;
}

export function plannerAiValue(status: CommandPlannerHostStatus | null): string {
  return status === null ? AUTO_VALUE : status.runtime_key;
}

export type PlannerChoiceResult =
  | { readonly kind: 'save'; readonly nodeId: string; readonly runtimeKey: string; readonly fallbackMode: 'none' | 'api' }
  | { readonly kind: 'clear'; readonly fallbackMode: 'none' | 'api' }
  | { readonly kind: 'hold'; readonly reason: string };

/**
 * A Planner Computer/AI change. Auto clears a stored host the way Clear Planner Host does.
 * A pair is saved only when that computer currently offers that runtime. Fallback mode is copied.
 */
export function plannerChoiceResult(input: {
  readonly status: CommandPlannerHostStatus | null;
  readonly computerId: string;
  readonly runtimeKey: string;
  readonly pairAvailable: boolean;
}): PlannerChoiceResult {
  const fallbackMode = input.status?.fallback_mode ?? 'none';
  if (input.computerId === AUTO_VALUE || input.runtimeKey === AUTO_VALUE) {
    if (input.status === null) return { kind: 'hold', reason: 'Auto leaves the Planner Host unset.' };
    return { kind: 'clear', fallbackMode };
  }
  if (input.status !== null && input.status.node_id === input.computerId && input.status.runtime_key === input.runtimeKey) {
    return { kind: 'hold', reason: 'That Planner Host is already stored.' };
  }
  if (!input.pairAvailable) {
    return { kind: 'hold', reason: 'That computer does not offer that AI. The stored Planner Host was not changed.' };
  }
  return { kind: 'save', nodeId: input.computerId, runtimeKey: input.runtimeKey, fallbackMode };
}

export function workerAiValue(preferenceKey: string | null): string {
  return preferenceKey ?? AUTO_VALUE;
}
